import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../../components-v2/Button';
import { Pill } from '../../../../../../../components-v2/Pill';
import { ButtonBase } from '../../../../../../../components/Button';
import { AIStarsIcon } from '../../../../../../../components/svgs/icons/AIStars';
import { PeopleIcon } from '../../../../../../../components/svgs/icons/PeopleIcon';
import { PlusIcon2 } from '../../../../../../../components/svgs/icons/PlusIcon';
import {
	getAdvancedCriteriaCount,
	getTagChips,
	ICampaignAudience,
	isAudienceEmpty,
} from '../../hooks/useCampaignAudiences';
import { AddTagPopover } from './AddTagPopover';
import { styleSheet } from './styles';

interface IProps {
	audience: ICampaignAudience;
	onRemoveTag: (name: string) => void;
	onAddTag: (name: string) => void;
	onEditAudience?: () => void;
	impersonationContext?: Api.IImpersonationContext;
}

export function CampaignAudienceRow({
	audience,
	onRemoveTag,
	onAddTag,
	onEditAudience,
	impersonationContext,
}: IProps): JSX.Element {
	const tagChips = getTagChips(audience);
	const advancedCriteriaCount = getAdvancedCriteriaCount(audience);
	const isEmpty = isAudienceEmpty(audience);
	const appliedTagNames = tagChips.map(chip => chip.name);

	return (
		<div className={css(styleSheet.audienceRowContainer)}>
			<span className={css(styleSheet.audienceRowLabel)}>
				<PeopleIcon
					className={css(isEmpty ? styleSheet.audienceRowLabelIconEmpty : styleSheet.audienceRowLabelIconFilled)}
					width={16}
					height={16}
				/>
				Audience
			</span>

			{isEmpty ? (
				<AddTagPopover
					appliedTagNames={appliedTagNames}
					onSelect={onAddTag}
					impersonationContext={impersonationContext}
					trigger={
						<Button variant='ghost' styles={[styleSheet.audienceRowAddButton]} aria-label='Add audience'>
							<PlusIcon2 width={12} height={12} />
							Add audience
							<em className={css(styleSheet.audienceRowEmptyHint)}>{' — none suggested'}</em>
						</Button>
					}
				/>
			) : (
				<>
					{tagChips.map(chip => (
						<Pill
							key={chip.name}
							variant='tag'
							color='blue'
							icon={chip.suggested ? <AIStarsIcon width={12} height={12} /> : undefined}
							label={chip.name}
							onRemove={onRemoveTag}
						/>
					))}
					<AddTagPopover
						appliedTagNames={appliedTagNames}
						onSelect={onAddTag}
						impersonationContext={impersonationContext}
						trigger={
							<Button variant='ghost' styles={[styleSheet.audienceRowAddButton]} aria-label='Add tag'>
								<PlusIcon2 width={12} height={12} />
								Add tag
							</Button>
						}
					/>
				</>
			)}

			{advancedCriteriaCount > 0 && (
				<span className={css(styleSheet.audienceRowAdvancedCount)}>
					{`+${advancedCriteriaCount} ${advancedCriteriaCount === 1 ? 'filter' : 'filters'}`}
				</span>
			)}

			{onEditAudience && (
				<ButtonBase className={css(styleSheet.audienceRowEditLink)} onClick={onEditAudience}>
					Edit audience
				</ButtonBase>
			)}

			{isEmpty && (
				<span className={css(styleSheet.audienceRowError)}>An audience is required to schedule this campaign.</span>
			)}
		</div>
	);
}
